import { BasicButton } from '../../Buttons/BasicButton';
import { DatePicker, DatePickerProps, TimeRangePickerProps } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import { FiltersContainer } from '../../../pages/Dashboard/styles';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { CircularProgress } from '@mui/material';
const { RangePicker } = DatePicker;

interface IFormValues {
  dateRange: [start: dayjs.Dayjs, end: dayjs.Dayjs];
}

interface IFilterDateDashboardProps {
  onClickExport: (startDate: Date, endDate: Date) => void;
  changeDate?: (dates: DatePickerProps['value'] | RangePickerProps['value']) => void;
  disabled?: boolean;
  isExporting?: boolean;
  defaultValue: [start: dayjs.Dayjs, end: dayjs.Dayjs];
}
export default function FilterDateDashboard({
  onClickExport,
  isExporting,
  changeDate,
  disabled,
  defaultValue,
}: IFilterDateDashboardProps) {
  const defaultValues: IFormValues = {
    dateRange: defaultValue ?? [dayjs().subtract(7, 'days'), dayjs()],
  };
  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Últimos 7 Dias', value: [dayjs().subtract(7, 'days'), dayjs().endOf('day')] },
    { label: 'Últimos 14 Dias', value: [dayjs().subtract(14, 'days'), dayjs().endOf('day')] },
    { label: 'Último mês', value: [dayjs().subtract(1, 'month'), dayjs().endOf('day')] },
    { label: 'Últimos 3 meses', value: [dayjs().subtract(3, 'months'), dayjs().endOf('day')] },
  ];
  const filterSchema = yup.object().shape({
    dateRange: yup
      .array()
      .of(
        yup
          .mixed<dayjs.Dayjs>()
          .test('is-dayjs', 'Data inválida', (value) => dayjs.isDayjs(value))
          .required('Data obrigatória'),
      )
      .min(2, 'Selecione o intervalo completo')
      .typeError('Selecione um intervalo de datas válido.')
      .required('Intervalo de datas é obrigatório'),
  });

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(filterSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const handleExportReport = (data: IFormValues) => {
    if (!data?.dateRange || data.dateRange.length < 2) {
      setError('dateRange', { message: 'Selecione um intervalo completo' });
      return;
    }
    const [startDate, endDate] = data.dateRange;
    onClickExport(startDate.toDate(), endDate.toDate());
  };

  const handleDateChange = (dates: DatePickerProps['value'] | RangePickerProps['value']) => {
    if (Array.isArray(dates)) {
      const [start, end] = dates;
      if (start && end) {
        changeDate?.([start, end]);
      }
    } else if (dates) {
      changeDate?.(dates);
    }
  };

  return (
    <FiltersContainer>
      <Controller
        control={control}
        name="dateRange"
        defaultValue={defaultValue}
        render={({ field: { onChange, value } }) => (
          <div>
            <RangePicker
              presets={rangePresets}
              disabled={disabled}
              defaultValue={defaultValue}
              value={value || undefined}
              maxDate={dayjs().endOf('day')}
              onChange={(date) => {
                handleDateChange(date);
                onChange(date);
              }}
              format="DD/MM/YYYY"
            />
            {errors.dateRange && <span className="fieldError">{errors.dateRange.message}</span>}
          </div>
        )}
      />
      <BasicButton
        disabled={disabled}
        variant="contained"
        size="large"
        onClick={handleSubmit(handleExportReport)}>
        {isExporting ? (
          <div className="button-text">
            Extraindo <CircularProgress style={{ color: 'white' }} size="15px" />
          </div>
        ) : (
          'Exportar'
        )}
      </BasicButton>
    </FiltersContainer>
  );
}
